import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { usePortal } from '../../contexts/PortalContext';
import { bulkChangeSalespersonCode } from '../../lib/bulkChangeSalespersonCode';
import { fetchSalespersonEmails } from '../../lib/applicationFormSettings';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { logPortalActivityQuiet } from '../../lib/portalActivityLog';
import { compareSalespersonCodes, formatSalespersonCode, normalizeSalespersonCodes } from '../../utils/salespersonScope';
import type { Client } from '../../types/crm';
import { isRootAccount } from '../../utils/accountHierarchy';

export type BulkSalespersonMode = 'from_to' | 'selected';

interface AccountBulkSalespersonModalProps {
  open: boolean;
  mode: BulkSalespersonMode;
  /** All clients in scope (for from→to preview and sub-company counts). */
  clients: Client[];
  selectedIds?: string[];
  onClose: () => void;
  onDone: (updatedCount: number) => void;
}

function uniqueCodesFromClients(clients: Client[]): string[] {
  const seen = new Set<string>();
  const list: string[] = [];
  for (const c of clients) {
    const formatted = formatSalespersonCode(c.owner_id ?? '');
    if (!formatted) continue;
    const key = normalizeSalespersonCodes([formatted])[0]?.toLowerCase() ?? '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push(formatted);
  }
  return list.sort(compareSalespersonCodes);
}

export function AccountBulkSalespersonModal({
  open,
  mode,
  clients,
  selectedIds = [],
  onClose,
  onDone,
}: AccountBulkSalespersonModalProps) {
  const { kind: portal } = usePortal();

  const [codeOptions, setCodeOptions] = useState<string[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [fromCode, setFromCode] = useState('');
  const [toCode, setToCode] = useState('');
  const [includeSubCompanies, setIncludeSubCompanies] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const masterCodes = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const raw of codeOptions) {
      const code = formatSalespersonCode(raw);
      if (!code) continue;
      const key = normalizeSalespersonCodes([code])[0]?.toLowerCase() ?? '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      list.push(code);
    }
    return list.sort(compareSalespersonCodes);
  }, [codeOptions]);

  const fromCodeOptions = useMemo(() => {
    const fromClients = uniqueCodesFromClients(clients);
    const seen = new Set(fromClients.map((c) => normalizeSalespersonCodes([c])[0]?.toLowerCase() ?? ''));
    const merged = [...fromClients];
    for (const code of masterCodes) {
      const key = normalizeSalespersonCodes([code])[0]?.toLowerCase() ?? '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(code);
    }
    return merged.sort(compareSalespersonCodes);
  }, [clients, masterCodes]);

  const selectedClients = useMemo(() => {
    if (mode !== 'selected') return [];
    const idSet = new Set(selectedIds.map(String));
    return clients.filter((c) => idSet.has(String(c.id)));
  }, [mode, selectedIds, clients]);

  const selectedParentCount = useMemo(
    () => selectedClients.filter((c) => isRootAccount(c)).length,
    [selectedClients],
  );

  const selectedSubCount = useMemo(() => {
    if (!includeSubCompanies || selectedParentCount === 0) return 0;
    const parentIds = new Set(
      selectedClients.filter((c) => isRootAccount(c)).map((c) => String(c.id)),
    );
    const selected = new Set(selectedClients.map((c) => String(c.id)));
    return clients.filter(
      (c) =>
        c.parent_client_id &&
        parentIds.has(String(c.parent_client_id)) &&
        !selected.has(String(c.id)),
    ).length;
  }, [clients, selectedClients, includeSubCompanies, selectedParentCount]);

  const fromMatchCount = useMemo(() => {
    if (mode !== 'from_to' || !fromCode) return 0;
    const fromNorm = normalizeSalespersonCodes([fromCode])[0] ?? '';
    if (!fromNorm) return 0;
    const matched = clients.filter((c) => {
      const own = normalizeSalespersonCodes([c.owner_id ?? ''])[0] ?? '';
      return own && own === fromNorm;
    });
    if (!includeSubCompanies) return matched.length;
    const idSet = new Set(matched.map((c) => String(c.id)));
    const parentIds = new Set(matched.filter((c) => isRootAccount(c)).map((c) => String(c.id)));
    let extra = 0;
    for (const c of clients) {
      if (!c.parent_client_id) continue;
      if (!parentIds.has(String(c.parent_client_id))) continue;
      if (idSet.has(String(c.id))) continue;
      extra += 1;
    }
    return matched.length + extra;
  }, [mode, fromCode, clients, includeSubCompanies]);

  const toCandidates = useMemo(() => {
    const exclude =
      mode === 'from_to' && fromCode
        ? (normalizeSalespersonCodes([fromCode])[0]?.toLowerCase() ?? '')
        : '';
    return masterCodes.filter((code) => {
      const key = normalizeSalespersonCodes([code])[0]?.toLowerCase() ?? '';
      return key && key !== exclude;
    });
  }, [masterCodes, mode, fromCode]);

  useEffect(() => {
    if (!open) return;
    setFromCode('');
    setToCode('');
    setIncludeSubCompanies(true);
    setError(null);
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCodesLoading(true);
    void fetchSalespersonEmails()
      .then((rows) => {
        if (cancelled) return;
        setCodeOptions(
          rows
            .filter((row) => row.active)
            .map((row) => row.code.trim())
            .filter(Boolean),
        );
      })
      .catch(() => {
        if (!cancelled) setCodeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toCode) {
      setError('Select the new salesperson code.');
      return;
    }
    if (mode === 'from_to' && !fromCode) {
      setError('Select the current salesperson code.');
      return;
    }
    if (mode === 'selected' && selectedIds.length === 0) {
      setError('Select at least one account.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await bulkChangeSalespersonCode({
        toCode,
        fromCode: mode === 'from_to' ? fromCode : null,
        clientIds: mode === 'selected' ? selectedIds : null,
        includeSubCompanies,
      });
      logPortalActivityQuiet({
        portal,
        action: 'update',
        entityType: 'account',
        entityId: mode === 'selected' ? selectedIds[0] : undefined,
        title:
          mode === 'from_to'
            ? `Bulk salesperson change: ${fromCode} → ${toCode}`
            : `Bulk salesperson change: ${selectedIds.length} account(s) → ${toCode}`,
        detail: [
          `Updated ${updated} account row(s).`,
          includeSubCompanies ? 'Included sub-companies / brands.' : 'Did not include sub-companies.',
        ].join('\n'),
      });
      onDone(updated);
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  const title =
    mode === 'from_to' ? 'Bulk change salesperson code' : 'Change salesperson code (selected)';

  const previewCount = mode === 'from_to' ? fromMatchCount : selectedClients.length + selectedSubCount;

  return (
    <Modal title={title} open={open} onClose={onClose}>
      <form className="crm-form" onSubmit={(e) => void handleSubmit(e)}>
        <p className="field-hint">
          Applies immediately for Admin / IT / Management. Only the account{' '}
          <strong>salesperson code</strong> is updated; project and task owners are unchanged.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        {mode === 'from_to' ? (
          <label className="field">
            <span className="field-label">From salesperson code *</span>
            <select
              className="field-input"
              value={fromCode}
              disabled={saving || codesLoading}
              onChange={(e) => {
                setFromCode(e.target.value);
                setToCode('');
              }}
            >
              <option value="">{codesLoading ? 'Loading…' : 'Select current code'}</option>
              {fromCodeOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="field-hint">
            {selectedIds.length} account{selectedIds.length === 1 ? '' : 's'} selected
            {selectedParentCount > 0
              ? ` (${selectedParentCount} parent${selectedParentCount === 1 ? '' : 's'})`
              : ''}
            .
          </p>
        )}

        <label className="field">
          <span className="field-label">To salesperson code *</span>
          <select
            className="field-input"
            value={toCode}
            disabled={saving || codesLoading || toCandidates.length === 0}
            onChange={(e) => setToCode(e.target.value)}
          >
            <option value="">{codesLoading ? 'Loading codes…' : 'Select new code'}</option>
            {toCandidates.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
          {!codesLoading && toCandidates.length === 0 ? (
            <p className="field-hint">
              No salesperson codes are available. Add codes under User management → Salesperson.
            </p>
          ) : null}
        </label>

        <label className="settings-sharepoint-form__toggle account-transfer-option">
          <input
            type="checkbox"
            checked={includeSubCompanies}
            onChange={(e) => setIncludeSubCompanies(e.target.checked)}
            disabled={saving}
          />
          <span>Include sub-companies / brands of matching parent accounts</span>
        </label>

        <p className="field-hint">
          Estimated impact: <strong>{previewCount}</strong> account
          {previewCount === 1 ? '' : 's'} will be updated
          {mode === 'from_to' && fromCode ? ` (currently ${fromCode})` : ''}.
        </p>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={
              saving ||
              !toCode ||
              (mode === 'from_to' && !fromCode) ||
              (mode === 'selected' && selectedIds.length === 0) ||
              previewCount === 0
            }
          >
            {saving ? 'Updating…' : 'Apply change'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
